import type { Tour } from 'nextstepjs';
import { ProjectShareDialogContent } from '../ProjectShareDialogContent';

// We use the `content` field to pass the i18n key to the custom card component.
// The `title` field is unused since our custom card handles all rendering via i18n.

export const buildTourSteps = (
  firstProjectId: string | null,
  openDialog: (content: React.ReactElement, className?: string) => void,
): (Tour & { beforeNext?: () => void; delay?: number })[] => {
  const projectBase = firstProjectId ? `/projects/${firstProjectId}` : null;

  const steps: Tour['steps'] = [
    {
      icon: null,
      title: '',
      content: 'welcome',
      showControls: false,
      showSkip: false,
    },
    {
      icon: null,
      title: '',
      content: 'projectList',
      selector: '#onboarding-project-list',
      side: 'right',
      showControls: false,
      showSkip: false,
      pointerPadding: 4,
      pointerRadius: 8,
      ...(projectBase ? { nextRoute: projectBase } : { nextRoute: '/settings' }),
    },
  ];

  if (projectBase) {
    steps.push(
      {
        icon: null,
        title: '',
        content: 'viewer',
        showControls: false,
        showSkip: false,
        prevRoute: '/',
      },
      {
        icon: null,
        title: '',
        content: 'headerTools',
        selector: '#onboarding-header-tools',
        side: 'bottom',
        showControls: false,
        showSkip: false,
        pointerPadding: 4,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: '',
        content: 'layerSidebar',
        selector: '#onboarding-layers-panel',
        side: 'left',
        showControls: false,
        showSkip: false,
        pointerPadding: 4,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: '',
        content: 'viewerTools',
        selector: '#onboarding-viewer-tools',
        side: 'top',
        showControls: false,
        showSkip: false,
        pointerPadding: 4,
        pointerRadius: 8,
        beforeNext: () => {
          openDialog(<ProjectShareDialogContent projectId={firstProjectId!} />, 'sm:max-w-[800px]');
        },
        delay: 120,
      },
      {
        icon: null,
        title: '',
        content: 'projectSharing',
        selector: 'div[role=dialog]',
        side: 'bottom',
        showControls: false,
        showSkip: false,
        nextRoute: `${projectBase}/edit`,
        pointerPadding: 8,
        pointerRadius: 12,
      },
      {
        icon: null,
        title: '',
        content: 'projectSettings',
        selector: '#onboarding-project-settings',
        side: 'top',
        showControls: false,
        showSkip: false,
        prevRoute: projectBase,
        nextRoute: `${projectBase}/upload`,
        pointerPadding: 4,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: '',
        content: 'uploadProcess',
        selector: '#onboarding-upload',
        side: 'top',
        showControls: false,
        showSkip: false,
        prevRoute: `${projectBase}/edit`,
        pointerPadding: 4,
        pointerRadius: 8,
      },
      {
        icon: null,
        title: '',
        content: 'batchProcessing',
        selector: '#onboarding-batch',
        side: 'top',
        showControls: false,
        showSkip: false,
        nextRoute: '/settings',
        pointerPadding: 4,
        pointerRadius: 8,
      },
    );
  }

  steps.push(
    {
      icon: null,
      title: '',
      content: 'orgSettings',
      selector: '#onboarding-org-settings',
      side: 'top',
      showControls: false,
      showSkip: false,
      ...(projectBase ? { prevRoute: `${projectBase}/upload` } : { prevRoute: '/' }),
      nextRoute: '/',
      pointerPadding: 4,
      pointerRadius: 8,
    },
    {
      icon: null,
      title: '',
      content: 'documentation',
      selector: '#onboarding-docs',
      side: 'right',
      showControls: false,
      showSkip: false,
      prevRoute: '/settings',
      pointerPadding: 4,
      pointerRadius: 8,
    },
  );

  return [{ tour: 'onboarding', steps }];
};
